/**
 * Hooks React Query para CRUD de dashboards + widgets. Usa service_role
 * implicitamente via RLS — o Superadmin tem policies permissivas em
 * `public.dashboards` e `public.dashboard_widgets`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { supabase } from '../../lib/supabase.js';

import {
  dashboardSchema,
  dashboardWidgetSchema,
  type CreateDashboardInput,
  type CreateWidgetInput,
  type Dashboard,
  type DashboardWidget,
} from './schemas.js';

export const DASHBOARD_KEYS = {
  list: () => ['dashboards'] as const,
  detail: (id: string) => ['dashboards', id] as const,
  widgets: (dashboardId: string) => ['dashboards', dashboardId, 'widgets'] as const,
};

const dashboardsListSchema = z.array(dashboardSchema);
const widgetsListSchema = z.array(dashboardWidgetSchema);

// ---------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------

export function useDashboards() {
  return useQuery({
    queryKey: DASHBOARD_KEYS.list(),
    staleTime: 30_000,
    queryFn: async (): Promise<Dashboard[]> => {
      const { data, error } = await supabase
        .from('dashboards')
        .select('*')
        .is('deleted_at', null)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return dashboardsListSchema.parse(data);
    },
  });
}

export function useDashboard(id: string | undefined) {
  return useQuery({
    queryKey: id ? DASHBOARD_KEYS.detail(id) : ['dashboards', 'pending'],
    enabled: !!id,
    staleTime: 30_000,
    queryFn: async (): Promise<Dashboard> => {
      const { data, error } = await supabase
        .from('dashboards')
        .select('*')
        .eq('id', id!)
        .is('deleted_at', null)
        .single();
      if (error) throw new Error(error.message);
      return dashboardSchema.parse(data);
    },
  });
}

export function useDashboardWidgets(dashboardId: string | undefined) {
  return useQuery({
    queryKey: dashboardId ? DASHBOARD_KEYS.widgets(dashboardId) : ['widgets', 'pending'],
    enabled: !!dashboardId,
    staleTime: 30_000,
    queryFn: async (): Promise<DashboardWidget[]> => {
      const { data, error } = await supabase
        .from('dashboard_widgets')
        .select('*')
        .eq('dashboard_id', dashboardId!)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return widgetsListSchema.parse(data);
    },
  });
}

// ---------------------------------------------------------------------
// Writes (mutations)
// ---------------------------------------------------------------------

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDashboardInput): Promise<Dashboard> => {
      const { data: userData } = await supabase.auth.getUser();
      const ownerId = userData.user?.id;
      if (!ownerId) throw new Error('Superadmin autenticado é necessário.');

      const { data, error } = await supabase
        .from('dashboards')
        .insert({
          owner_id: ownerId,
          scope: input.scope,
          clinic_id: input.clinic_id,
          name: input.name,
          description: input.description,
          is_shared: input.is_shared,
          layout: {},
        })
        .select('*')
        .single();
      if (error) throw new Error(error.message);
      return dashboardSchema.parse(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DASHBOARD_KEYS.list() }),
  });
}

export function useUpdateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      description?: string | null;
      is_shared?: boolean;
    }) => {
      const { id, ...patch } = input;
      const { data, error } = await supabase
        .from('dashboards')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw new Error(error.message);
      return dashboardSchema.parse(data);
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: DASHBOARD_KEYS.list() });
      qc.invalidateQueries({ queryKey: DASHBOARD_KEYS.detail(d.id) });
    },
  });
}

export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('dashboards')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw new Error(error.message);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DASHBOARD_KEYS.list() }),
  });
}

export function useCreateWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateWidgetInput): Promise<DashboardWidget> => {
      // `config` e `position` são jsonb — o tipo do Supabase aceita `Json`
      // (any recursivo). Cast local porque o `exactOptionalPropertyTypes`
      // não casa com nosso shape Zod estrito.
      const row = {
        dashboard_id: input.dashboard_id,
        widget_type: input.widget_type,
        title: input.title,
        subtitle: input.subtitle,
        query_ref: input.query_ref,
        config: input.config as unknown,
        position: input.position as unknown,
      };
      const { data, error } = await supabase
        .from('dashboard_widgets')
        .insert(row as never)
        .select('*')
        .single();
      if (error) throw new Error(error.message);
      return dashboardWidgetSchema.parse(data);
    },
    onSuccess: (w) => qc.invalidateQueries({ queryKey: DASHBOARD_KEYS.widgets(w.dashboard_id) }),
  });
}

export function useUpdateWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<DashboardWidget> & { id: string; dashboard_id: string }) => {
      const { id, dashboard_id: _d, ...patch } = input;
      const { data, error } = await supabase
        .from('dashboard_widgets')
        .update(patch as never)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw new Error(error.message);
      return dashboardWidgetSchema.parse(data);
    },
    onSuccess: (w) => qc.invalidateQueries({ queryKey: DASHBOARD_KEYS.widgets(w.dashboard_id) }),
  });
}

export function useDeleteWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (w: { id: string; dashboard_id: string }) => {
      const { error } = await supabase.from('dashboard_widgets').delete().eq('id', w.id);
      if (error) throw new Error(error.message);
      return w;
    },
    onSuccess: (w) => qc.invalidateQueries({ queryKey: DASHBOARD_KEYS.widgets(w.dashboard_id) }),
  });
}
